/**
 * The Scientist's voice: pre-rendered TTS lines (scripts/tts-narrator.mjs →
 * public/audio/voice/<voiceKey>.mp3 + manifest.json). A line that isn't rendered plays
 * nothing — never a synthetic stand-in.
 */
import type { NarratorMood } from '@core/services';
import type { AudioGraph } from './graph';
import { burpFraction, voiceKey } from './voiceKey';

const BASE = `${import.meta.env.BASE_URL}audio/voice/`;
const CACHE = 24;
/** Subtle mood colouring via playback rate (keeps the same voice). */
const RATE: Record<NarratorMood, number> = { snark: 1, alarm: 1.05, proud: 1.01, bored: 0.97, excited: 1.04 };

export interface Line {
  /** Seconds until the line ends (from now). */
  duration: number;
  /** Seconds from now at which each *burp* should sound. */
  burps: number[];
  stop(): void;
}

export class Narration {
  private manifest?: Promise<Record<string, { d: number }>>;
  private cache = new Map<string, Promise<AudioBuffer | null>>();
  private current?: { src: AudioBufferSourceNode; gain: GainNode };
  private seq = 0;

  constructor(private readonly graph: AudioGraph) {}

  private lines(): Promise<Record<string, { d: number }>> {
    this.manifest ??= fetch(`${BASE}manifest.json`)
      .then((r) => (r.ok ? r.json() : { lines: {} }))
      .then((m) => m.lines ?? {})
      .catch(() => ({}));
    return this.manifest;
  }

  warm(): void {
    void this.lines();
  }

  private buffer(key: string): Promise<AudioBuffer | null> {
    let p = this.cache.get(key);
    if (!p) {
      p = fetch(`${BASE}${key}.mp3`)
        .then((r) => (r.ok ? r.arrayBuffer() : Promise.reject(new Error(String(r.status)))))
        .then((ab) => this.graph.ctx.decodeAudioData(ab))
        .catch(() => null);
      this.cache.set(key, p);
      if (this.cache.size > CACHE) this.cache.delete(this.cache.keys().next().value!);
    }
    return p;
  }

  /** Is there a rendered line for this text? */
  async has(text: string): Promise<boolean> {
    return voiceKey(text) in (await this.lines());
  }

  /**
   * Speak `text`. Resolves with timing info once playback starts, or null when the line
   * isn't rendered (or was superseded by a newer line).
   */
  async speak(text: string, mood: NarratorMood = 'snark'): Promise<Line | null> {
    const token = ++this.seq;
    this.stop();
    const key = voiceKey(text);
    if (!(key in (await this.lines()))) {
      if (import.meta.env.DEV) console.info(`[audio] no voice line ${key}: ${text.slice(0, 60)}`);
      return null;
    }
    const buf = await this.buffer(key);
    if (!buf || token !== this.seq) return null;
    const ctx = this.graph.ctx;
    const rate = RATE[mood] ?? 1;
    const src = ctx.createBufferSource();
    src.buffer = buf;
    src.playbackRate.value = rate;
    const gain = ctx.createGain();
    src.connect(gain).connect(this.graph.voice);
    const t = ctx.currentTime + 0.01;
    src.start(t);
    this.current = { src, gain };
    src.onended = () => {
      if (this.current?.src === src) this.current = undefined;
    };
    const duration = buf.duration / rate;
    return {
      duration,
      burps: burpFraction(text).map((f) => Math.min(duration, f * duration)),
      stop: () => this.stop(),
    };
  }

  stop(): void {
    const c = this.current;
    if (!c) return;
    this.current = undefined;
    const t = this.graph.now;
    c.gain.gain.setTargetAtTime(0, t, 0.03);
    c.src.stop(t + 0.15);
  }
}
